import { useState, useMemo } from "react";
import { PollQuestion, PollAnswer } from "../interfaces/Poll";

export interface AnswerState {
  question_id?: number;
  required: boolean;
  answered: boolean;
  answer_id?: number;
  description?: string;
}

// useTryPoll: Custom hook to manage the state of a poll. Used in TryPollDialog.
export const useTryPoll = (questions: PollQuestion[]) => {
  const [answers, setAnswers] = useState<AnswerState[]>(
    questions.map((q) => ({
      question_id: q.id,
      required: !!q.required,
      answered: false,
      answer_id: undefined,
      description: undefined,
    }))
  );

  // Calculate the numeric result based on the current answers
  const result = useMemo(() => {
    return answers.reduce((sum, a) => {
      if (!a.answered || a.question_id == null || a.answer_id == null)
        return sum;

      const question = questions.find((q) => q.id === a.question_id);
      if (!question) return sum;

      const selected = question.answers?.find((pa) => pa.id === a.answer_id);
      if (!selected) return sum;

      const raw = selected.value;
      const num =
        raw == null ? NaN : typeof raw === "number" ? raw : Number(raw);

      return sum + (Number.isFinite(num) ? num : 0);
    }, 0);
  }, [answers, questions]);

  // Function to update an answer
  const updateAnswer = (answer: PollAnswer | undefined) => {
    setAnswers((oldAnswers) =>
      oldAnswers.map((a) =>
        a.question_id === answer?.inquiry_question_id
          ? {
              ...a,
              answered: !!answer,
              answer_id: answer?.id,
              description: answer?.description,
            }
          : a
      )
    );
  };

  return { answers, result, updateAnswer };
};
